import { AddCircleIcon, DownloadIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { IDV_OVERALL_STATUS } from 'constants/idv';
import { Permissions } from 'constants/permissions';
import { saveAs } from 'file-saver';
import { useAccessControl } from 'hooks/access-control';
import { useTracking } from 'hooks/analytic-tracking';
import JSZip from 'jszip';
import JSZipUtils from 'jszip-utils';
import { useSelector } from 'react-redux';

import { CheckStatusRow } from 'components/CheckStatusRow/CheckStatusRow';
import { Button } from 'components/FormComponents';

import { useLazyGetCandidateCheckResultQuery } from 'api/candidate-checks-api';
import { useLazyGetCandidateUploadsQuery } from 'api/candidates-api';
import { useLazyGetSubCheckResultQuery } from 'api/sub-checks-api';
import { selectPermissions } from 'api/user-permissions-slice';

import styles from './Checks.module.scss';

export const Checks = ({
  candidate,
  company,
  companyChecks,
  checks = [],
  user,
  setLoading = () => {},
  showNewChecksModal = () => {},
  handleCancelCheck = () => {},
  handleCheckUpdate = () => {},
  handleSubCheckUpdate = () => {},
}) => {
  const permissions = useSelector(selectPermissions);
  const hasAccess = useAccessControl(permissions);

  const [getCandidateCheckResult] = useLazyGetCandidateCheckResultQuery();
  const [getSubCheckResult] = useLazyGetSubCheckResultQuery();

  const [getUploads] = useLazyGetCandidateUploadsQuery();

  const downloadUpload = upload => {
    return new Promise((resolve, reject) => {
      JSZipUtils.getBinaryContent(upload.url, (error, data) => {
        if (error) reject(error);
        else resolve({ file_name: upload.file_name, data });
      });
    });
  };

  const handleDownloadIds = async () => {
    setLoading(true);
    const zip = new JSZip();
    const uploads = await getUploads({ id: candidate.id }).unwrap();

    // Only allow ID uploads whose id_upload_detail has status verified or not_applicable (same as report attachments)
    const allowedStatuses = [IDV_OVERALL_STATUS.VERIFIED, IDV_OVERALL_STATUS.NOT_APPLICABLE];
    const allowedUploadIds = new Set();
    uploads.forEach(upload => {
      const detail = upload.id_upload_detail;
      if (detail && allowedStatuses.includes(detail.status)) {
        allowedUploadIds.add(upload.id);
        if (detail.back_upload_id) {
          allowedUploadIds.add(detail.back_upload_id);
        }
      }
    });

    const promises = uploads
      .filter(upload => upload.metadata?.id_type && allowedUploadIds.has(upload.id))
      .map(upload => downloadUpload(upload));

    const results = await Promise.all(promises);
    results.forEach((result, index) => {
      zip.file(`${index + 1}. ${result.file_name}`, result.data, { binary: true });
    });
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `${candidate.name} ids.zip`);

    setLoading(false);
  };

  return (
    <div className="bg-neutral-2 rounded-3xl border border-border-primary">
      <div className="flex flex-row gap-2 justify-between items-center p-8 bg-neutral-1 rounded-t-3xl border-b border-border-primary">
        <div className="flex flex-col gap-1">
          <div className="text-text-primary text-[18px] font-semibold">Background Checks</div>
          <div className="text-text-tertiary text-[14px] font-light">
            Track all background checks for this candidate, view and download the results, and
            manage the checks.
          </div>
        </div>
        <div className="flex flex-row gap-2">
          {hasAccess(Permissions.CandidateIdsView) && (
            <Button
              type="outline"
              className={cn(styles.buttonWidth, 'u-margin-right')}
              onClick={handleDownloadIds}>
              <HugeiconsIcon icon={DownloadIcon} className="mr-2" size="20" strokeWidth="1.5" /> ID
              Documents
            </Button>
          )}
          {hasAccess(Permissions.CandidateWrite) && (
            <Button onClick={showNewChecksModal} className={cn(styles.buttonWidth)}>
              <HugeiconsIcon icon={AddCircleIcon} className="mr-2" /> Add Check
            </Button>
          )}
        </div>
      </div>
      <div className="px-8 pb-8">
        {checks.map(check => {
          const companyCheck = companyChecks.find(companyCheck => companyCheck.type == check.type);
          const checkType = companyCheck?.check_type;
          if (!checkType) return;

          return (
            <CheckStatusRow
              key={check.id}
              candidate={candidate}
              checkType={checkType}
              companyCheck={companyCheck}
              permissions={permissions}
              company={company}
              check={check}
              user={user}
              setLoading={setLoading}
              handleCheckUpdate={handleCheckUpdate}
              getCandidateCheckResult={getCandidateCheckResult}
              handleSubCheckUpdate={handleSubCheckUpdate}
              getSubCheckResult={getSubCheckResult}
              handleCancelCheck={handleCancelCheck}
            />
          );
        })}
      </div>
    </div>
  );
};
